import { queryKeys } from '@query/keys'
import { useQuery } from '@tanstack/react-query'

import { useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'

import { CurrencyType } from '@model/WalletDto'

interface UseIntroducingBrokerWalletBreakdownProps {
  selectedWalletId?: string
  selectedWalletCurrency?: CurrencyType
}

interface UseIntroducingBrokerWalletBreakdownResult {
  incomingReserved: number
  allTimeReward: number
  isLoading: boolean
  isError: boolean
}

export const useIntroducingBrokerWalletBreakdown = ({
  selectedWalletId,
  selectedWalletCurrency,
}: UseIntroducingBrokerWalletBreakdownProps): UseIntroducingBrokerWalletBreakdownResult => {
  const apiClient = useApiClient(ClientApiClient)

  const brokerSummaryQuery = useQuery({
    queryKey: [...queryKeys.ib.wallets(), 'incomingReserved'],
    queryFn: () => apiClient.getIntroducingBrokerSummary(),
    enabled: !!selectedWalletId,
    refetchOnMount: 'always',
  })

  const walletRewardQuery = useQuery({
    queryKey: [...queryKeys.ib.wallets(), 'reward', selectedWalletId ?? null],
    queryFn: () => apiClient.getIntroducingBrokerTransactionsSummary(selectedWalletId!),
    enabled: !!selectedWalletId,
    refetchOnMount: 'always',
  })

  const incomingReserved =
    selectedWalletCurrency === 'USD'
      ? (brokerSummaryQuery.data?.amount ?? 0) + (walletRewardQuery.data?.pendingReward ?? 0)
      : 0

  return {
    incomingReserved,
    allTimeReward: walletRewardQuery.data?.allTimeReward ?? 0,
    isLoading: !!selectedWalletId && (brokerSummaryQuery.isLoading || walletRewardQuery.isLoading),
    isError:
      (brokerSummaryQuery.isError && brokerSummaryQuery.data === undefined) ||
      (walletRewardQuery.isError && walletRewardQuery.data === undefined),
  }
}
